/**
 * Step 2, NOW (2.J deliverable 13): the current NOW, a field read by `chrono.preview` 250 ms after
 * the last keystroke, and "Set NOW", built as 1.O-1's NOW chip builds it with reason `bootstrap`.
 * The first NOW creates the clock and can't be undone; a later one is undone with `clock.undo`.
 */
import {
  ApiError,
  type ClockV1,
  chronoPreview,
  clockAdvance,
  clockUndo,
  NetworkError,
} from '@codex/contracts';
import { Banner, Button, TextField } from '@codex/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../auth/client.js';
import { useGoTo } from '../../shell/open-doc.js';
import { useClockQuery, useWhenContext, whenKeys } from '../../when/calendar-context.js';
import { clockPlan, nowText } from '../../when/now-model.js';
import {
  NO_CALENDAR,
  NOW_CONFLICT,
  NOW_FAILED,
  NOW_UNREAD,
  NOW_UNSET,
  nowIsText,
  reasonText,
} from '../../when/words.js';
import { useDraft } from '../drafts.js';
import { nowRows } from '../model/step-undo.js';
import { AppliedList } from '../step-undo.js';

export const NOW_PREVIEW_MS = 250;

/** `clock.undo` of one advance, against the clock row version the list was read at. */
export function undoAdvance(id: string, rv: number): Promise<ClockV1> {
  return api(clockUndo, { params: { id }, body: { baseRv: rv } });
}

export function NowStep() {
  const ctx = useWhenContext();
  const clockQ = useClockQuery();
  const client = useQueryClient();
  const goTo = useGoTo();
  const [text, setText] = useDraft('now:text', '');
  const [settled, setSettled] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [createdClock, setCreatedClock] = useDraft('now:created', false);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(text), NOW_PREVIEW_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const read = useQuery({
    queryKey: ['bootstrap', 'now-preview', settled],
    queryFn: ({ signal }) => api(chronoPreview, { body: { input: settled } }, { signal }),
    enabled: settled.trim() !== '' && ctx.status === 'ready',
    retry: false,
  });

  if (ctx.status !== 'ready' || ctx.calendar === null) {
    return (
      <div className="cx-bootstrap__step">
        <p role="status">
          {ctx.status === 'loading' ? 'Loading the calendar…' : `${NO_CALENDAR} NOW needs one.`}
        </p>
        {ctx.status === 'none' && (
          <a href="/calendar" onClick={(event) => goTo('/calendar', event)}>
            Set up the calendar
          </a>
        )}
      </div>
    );
  }
  const calendar = ctx.calendar;
  const clock: ClockV1 | null = clockQ.data ?? null;
  const plan = clockPlan(
    { kind: 'set', text },
    { calendar, now: ctx.now, clock, nowState: ctx.nowState },
  );
  const current = settled === text ? read.data : undefined;
  const ready =
    plan.ok &&
    current !== undefined &&
    current.status !== 'invalid' &&
    current.status !== 'undated' &&
    current.status !== 'pending' &&
    (current.kind === 'point' || current.kind === 'now');
  const sentence =
    text.trim() === ''
      ? null
      : !plan.ok
        ? plan.reason
        : current === undefined
          ? 'Reading…'
          : current.status === 'invalid'
            ? reasonText(current.reason ?? 'empty')
            : `NOW will read ${plan.tick === null ? (current.canonical ?? text) : nowText(calendar, plan.tick)}.`;

  const send = async () => {
    if (!plan.ok || !ready || busy) return;
    setBusy(true);
    setNotice(null);
    setMessage(null);
    try {
      const next = await api(clockAdvance, { body: { ...plan.request, reason: 'bootstrap' } });
      client.setQueryData(whenKeys.clock, next);
      if (plan.request.baseRv === 0) setCreatedClock(true);
      setMessage(
        next.presentTick === null ? NOW_UNSET : nowIsText(nowText(calendar, next.presentTick)),
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        await client.invalidateQueries({ queryKey: whenKeys.clock });
        setNotice(NOW_CONFLICT);
      } else {
        setNotice(error instanceof NetworkError ? NOW_UNREAD : NOW_FAILED);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="cx-bootstrap__step"
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
    >
      <p className="cx-bootstrap__muted">
        Set today's date in the world. It is the point in time the campaign has reached.
      </p>
      <p data-testid="bootstrap.now.current">
        {ctx.nowState === 'set' && ctx.now !== null
          ? nowIsText(nowText(calendar, ctx.now))
          : ctx.nowState === 'unknown'
            ? NOW_UNREAD
            : 'Not set'}
      </p>
      <TextField
        label="NOW"
        data-testid="bootstrap.now.input"
        autoComplete="off"
        spellCheck={false}
        placeholder="14 Ember 1042, 20:00"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setMessage(null);
        }}
      />
      <p role="status" className="cx-bootstrap__muted" data-testid="bootstrap.now.sentence">
        {sentence}
      </p>
      <div className="cx-bootstrap__actions">
        <Button
          variant="primary"
          type="submit"
          data-testid="bootstrap.now.set"
          busy={busy}
          disabled={!ready}
        >
          Set NOW
        </Button>
      </div>
      {message !== null && <p role="status">{message}</p>}
      {notice !== null && (
        <Banner tone="warning" live="assertive">
          {notice}
        </Banner>
      )}
      <AppliedList
        rows={nowRows(clock, createdClock)}
        undo={async (row) => {
          if (clock === null) return;
          const next = await undoAdvance(row.id, clock.rv);
          client.setQueryData(whenKeys.clock, next);
        }}
        onUndone={() => setMessage(null)}
      />
    </form>
  );
}
